import { TenantRole, TenantUserSettings } from '@/api/generatedApi';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useAuthService } from '@/services/auth';
import { createUseStyles } from '@/services/createUseStyles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Theme,
  Typography,
} from '@mui/material';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenants } from '@/services/tenants/useTenants';
import { useIndexPage } from '@/hooks/useIndexPage';

const useStyles = createUseStyles((theme: Theme) => ({
  TenantsPage_loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  TenantsPage_dialogTitle: {
    paddingBottom: theme.spacing(1),
  },
  TenantsPage_dialogContent: {
    paddingBottom: theme.spacing(2),
  },
  TenantsPage_instructionText: {
    marginBottom: theme.spacing(3),
  },
  TenantsPage_formControlLabel: {
    margin: 0,
    padding: 0,
    '& .MuiFormControlLabel-label': {
      width: '100%',
      padding: 0,
    },
  },
  TenantsPage_tenantOption: {
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    width: '100%',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  TenantsPage_tenantOptionSelected: {
    backgroundColor: theme.palette.background.default,
  },
  TenantsPage_dialogActions: {
    paddingTop: theme.spacing(1),
    gap: theme.spacing(1),
  },
}));

export const SPINNER_ID = 'loading-spinner';
export const TENANT_SELECTION_DIALOG_ID = 'tenant-selection-dialog';
export const CANCEL_BUTTON_ID = 'cancel-button';
export const SELECT_TENANT_BUTTON_ID = 'select-tenant-button';

export const TenantsPage = () => {
  const c = useStyles();
  const { logout } = useAuthService();
  const navigate = useNavigate();
  const { setTenant, getTenantId } = useTenants();
  const { data: user, isLoading } = useGetCurrentUser();
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantUserSettings | undefined>();
  const currentlySelectedTenantId = getTenantId();
  const { indexPagePath } = useIndexPage();

  const allSettings = user?.user?.settings?.filter(tenant => tenant.userEnabled);
  const tenantSettings = allSettings?.filter(tenant => tenant.tenantRole !== TenantRole.Search);

  const handleSelectTenant = () => {
    setShowTenantModal(false);
    if (selectedTenant) {
      setTenant(selectedTenant);
      navigate(indexPagePath);
    }
  };

  const handleCancelTenantSelection = () => {
    logout();
    setShowTenantModal(false);
  };

  useEffect(() => {
    if (!allSettings || !tenantSettings || isLoading) {
      return;
    } else if (allSettings.length === 0) {
      navigate('/access-denied');
    } else if (allSettings.length === 1) {
      const onlyTenant = allSettings[0];
      setTenant(onlyTenant);
      navigate(indexPagePath);
    } else if (tenantSettings.length === 1) {
      const onlyTenant = tenantSettings[0];
      setTenant(onlyTenant);
      navigate(indexPagePath);
    } else if (tenantSettings.length > 1) {
      if (currentlySelectedTenantId) {
        const previouslySelectedTenant = tenantSettings.find(tenant => tenant.tenantId === currentlySelectedTenantId);
        if (previouslySelectedTenant) {
          setTenant(previouslySelectedTenant);
          navigate(indexPagePath);
        } else {
          setShowTenantModal(true);
        }
      } else {
        setShowTenantModal(true);
      }
    }
  }, [user?.user?.settings, setTenant, navigate, currentlySelectedTenantId, indexPagePath]);

  if (isLoading) {
    return (
      <Box className={c.TenantsPage_loadingContainer}>
        <CircularProgress size={24} color='inherit' data-testid={SPINNER_ID} />
      </Box>
    );
  }

  return (
    <Dialog
      open={showTenantModal}
      onClose={handleCancelTenantSelection}
      maxWidth='sm'
      fullWidth
      data-testid={TENANT_SELECTION_DIALOG_ID}
    >
      <DialogTitle className={c.TenantsPage_dialogTitle}>
        <Typography variant='h5' component='div' fontWeight='500'>
          Select Tenant
        </Typography>
      </DialogTitle>

      <DialogContent className={c.TenantsPage_dialogContent}>
        <RadioGroup
          value={selectedTenant?.tenantId || ''}
          onChange={event =>
            setSelectedTenant(tenantSettings?.find(tenant => tenant.tenantId === event.target.value) || undefined)
          }
        >
          {tenantSettings?.map(tenant => (
            <FormControlLabel
              key={tenant.tenantId}
              value={tenant.tenantId}
              control={<Radio />}
              className={c.TenantsPage_formControlLabel}
              label={
                <Box
                  className={classNames(c.TenantsPage_tenantOption, {
                    [c.TenantsPage_tenantOptionSelected]: selectedTenant?.tenantId === tenant.tenantId,
                  })}
                >
                  <Typography variant='h6' component='div' fontWeight='500'>
                    {tenant.tenantDisplayName}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </DialogContent>

      <DialogActions className={c.TenantsPage_dialogActions}>
        <Button onClick={handleCancelTenantSelection} color='inherit' data-testid={CANCEL_BUTTON_ID}>
          CANCEL
        </Button>
        <Button
          onClick={handleSelectTenant}
          variant='contained'
          disabled={!selectedTenant}
          data-testid={SELECT_TENANT_BUTTON_ID}
        >
          SELECT TENANT
        </Button>
      </DialogActions>
    </Dialog>
  );
};
